import { supabase } from '../lib/supabase';
import { initializeFreshUserData } from './localStorage';
import { getRedirectUrl } from './urlConfig';



// Helper to translate Supabase errors
const translateSupabaseError = (message) => {
  if (!message) return 'Ha ocurrido un error desconocido.';

  if (message.includes('User already registered')) return 'Este correo ya está registrado.';
  if (message.includes('Invalid login credentials')) return 'Correo o contraseña incorrectos.';
  if (message.includes('Email not confirmed')) return 'Debes confirmar tu correo electrónico.';
  if (message.includes('Password should be')) return 'La contraseña es muy débil.';

  return message; // Fallback to original if no translation found
};

// Check if user is logged in
export const isUserLoggedIn = async () => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    return !!session;
  } catch (error) {
    console.error('Error checking login status:', error);
    return false;
  }
};

// Get current user
export const getCurrentUser = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    return user;
  } catch (error) {
    console.error('Error getting current user:', error);
    return null;
  }
};

// Register new user
export const registerUser = async (email, password, name = '') => {
  try {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: name || email.split('@')[0],
        }
      }
    });

    if (error) throw error;

    // Initialize fresh user data in localStorage for new users
    if (data.user) {
      initializeFreshUserData();
      console.log('✅ New user registered with fresh data');
    }

    return { success: true, user: data.user };
  } catch (error) {
    console.error('Error registering user:', error);
    return { success: false, error: translateSupabaseError(error.message) || 'Error al registrar usuario' };
  }
};

// Login user
export const loginUser = async (email, password) => {
  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) throw error;

    // Actualizar última sesión para el sistema de notificaciones de inactividad
    if (data.user) {
      try {
        await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/api/auth/update-session`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ userId: data.user.id }),
        });
      } catch (sessionError) {
        console.error('Error updating session:', sessionError);
        // No lanzar error, solo registrar
      }
    }

    return { success: true, user: data.user };
  } catch (error) {
    console.error('Error logging in:', error);
    return { success: false, error: translateSupabaseError(error.message) || 'Error al iniciar sesión' };
  }
};

// Login with Google
export const loginWithGoogle = async () => {
  try {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: getRedirectUrl()
      }
    });

    if (error) throw error;

    return { success: true, data };
  } catch (error) {
    console.error('Error logging in with Google:', error);
    return { success: false, error: translateSupabaseError(error.message) || 'Error al iniciar sesión con Google' };
  }
};

// Logout user
export const logoutUser = async () => {
  try {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;

    // Clear all game data from localStorage on logout
    const { clearAllGameData } = await import('./localStorage');
    clearAllGameData();
    console.log('✅ User logged out and localStorage cleared');

    return { success: true };
  } catch (error) {
    console.error('Error logging out:', error);
    return { success: false, error: error.message || 'Error al cerrar sesión' };
  }
};

// Update user profile
export const updateUserProfile = async (updates) => {
  try {
    const { data, error } = await supabase.auth.updateUser({
      data: updates
    });

    if (error) throw error;

    return { success: true, user: data.user };
  } catch (error) {
    console.error('Error updating user profile:', error);
    return { success: false, error: error.message || 'Error al actualizar perfil' };
  }
};

// Delete user account
export const deleteUserAccount = async (userId) => {
  try {
    // Determinar URL base correcta, evitando doble /api
    let apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
    if (apiUrl.endsWith('/')) apiUrl = apiUrl.slice(0, -1);

    // Si la URL ya incluye /api, usamos esa base, si no, lo agregamos? 
    // Para asegurar compatibilidad con la configuración actual:
    // Si apiUrl termina en /api, la ruta es /auth/delete-account
    // Si apiUrl es el host base, la ruta es /api/auth/delete-account

    // Simplificación: Usar URL relativa si es posible o construirla basándose en REACT_APP_API_URL que sabemos es .../api
    const endpoint = `${apiUrl}/auth/delete-account`;

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ userId }),
    });

    const data = await response.json();
    if (!data.success) throw new Error(data.error);

    // Call logout to clear local state and Supabase session
    await logoutUser();

    return { success: true };
  } catch (error) {
    console.error('Error deleting account:', error);
    return { success: false, error: error.message || 'Error al eliminar cuenta' };
  }
};

